import React from 'react';
import { Sparklines, SparklinesLine } from 'react-sparklines';

const formatMetric = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
};

const scoreTone = (value) => {
  if (typeof value !== 'number') {
    return '#556970';
  }
  if (value >= 80) return '#1f7a4d';
  if (value >= 60) return '#214f77';
  if (value >= 40) return '#8d6c16';
  return '#aa3d33';
};

const AssetQuickView = ({ asset }) => {
  const [history, setHistory] = React.useState([]);
  const symbol = asset?.code || asset?.symbol;

  React.useEffect(() => {
    if (symbol) {
      fetch(`/api/asset-metrics/${symbol}/history`)
        .then((res) => (res.ok ? res.json() : []))
        .then((data) => setHistory(Array.isArray(data) ? data : []))
        .catch(() => setHistory([]));
    } else {
      setHistory([]);
    }
  }, [symbol]);

  if (!asset) {
    return (
      <div className="asset-quick-view asset-quick-view--empty">
        <div className="asset-quick-view__eyebrow">Quick View</div>
        <h3>Actif en focus</h3>
        <div className="asset-quick-view__placeholder">
          Selectionnez un actif pour voir le detail.
        </div>
      </div>
    );
  }

  const assetLabel = asset.code || asset.symbol;
  const priceHistory = history
    .map((entry) => entry?.price)
    .filter((value) => typeof value === 'number' && !Number.isNaN(value));
  const scoreHistory = history
    .map((entry) => entry?.compositeScore ?? entry?.score)
    .filter((value) => typeof value === 'number' && !Number.isNaN(value));

  return (
    <div className="asset-quick-view">
      <div className="asset-quick-view__eyebrow">Quick View</div>
      <h3>{assetLabel}</h3>
      {asset.name ? <div className="asset-quick-view__subtitle">{asset.name}</div> : null}

      {(priceHistory.length > 0 || scoreHistory.length > 0) && (
        <div className="asset-quick-view__charts">
          {priceHistory.length > 0 ? (
            <div className="asset-quick-view__chart-card">
              <span className="asset-quick-view__chart-label">Prix recent</span>
              <Sparklines data={priceHistory} width={140} height={36} margin={4}>
                <SparklinesLine color="#214f77" style={{ strokeWidth: 2, fill: 'none' }} />
              </Sparklines>
            </div>
          ) : null}
          {scoreHistory.length > 0 ? (
            <div className="asset-quick-view__chart-card">
              <span className="asset-quick-view__chart-label">Score composite</span>
              <Sparklines data={scoreHistory} width={140} height={36} margin={4}>
                <SparklinesLine color={scoreTone(asset.compositeScore)} style={{ strokeWidth: 2, fill: 'none' }} />
              </Sparklines>
            </div>
          ) : null}
        </div>
      )}

      <div className="asset-quick-view__metrics">
        <div className="asset-quick-view__metric"><span>Prix</span><strong>{formatMetric(asset.price)}</strong></div>
        <div className="asset-quick-view__metric"><span>Performance 24h</span><strong>{formatMetric(asset.perf24h)}</strong></div>
        <div className="asset-quick-view__metric"><span>Performance 7j</span><strong>{formatMetric(asset.perf7d)}</strong></div>
        <div className="asset-quick-view__metric"><span>Momentum</span><strong style={{ color: scoreTone(asset.momentumScore) }}>{formatMetric(asset.momentumScore)}</strong></div>
        <div className="asset-quick-view__metric"><span>Volatilite</span><strong style={{ color: scoreTone((asset.volatility ?? 0) * 10) }}>{formatMetric(asset.volatility)}</strong></div>
        <div className="asset-quick-view__metric"><span>Risk</span><strong>{formatMetric(asset.riskScore)}</strong></div>
        <div className="asset-quick-view__metric"><span>Pattern</span><strong>{asset.detectedPattern ?? '—'}</strong></div>
        <div className="asset-quick-view__metric"><span>Pattern Score</span><strong>{formatMetric(asset.patternScore)}</strong></div>
        <div className="asset-quick-view__metric"><span>Composite Score</span><strong style={{ color: scoreTone(asset.compositeScore) }}>{formatMetric(asset.compositeScore)}</strong></div>
        <div className="asset-quick-view__metric"><span>Correlation BTC</span><strong>a venir</strong></div>
      </div>
    </div>
  );
};

export default AssetQuickView;
